import { DocumentSnapshot, QuerySnapshot } from "firebase/firestore";
import { ZodIssue, ZodType, ZodTypeDef } from "zod";

import { logError } from "shared/services/ErrorReporting";

const MAX_REPORTED_ISSUES = 5;
const MAX_REPORTED_DOCUMENTS = 200;

const describeIssue = (issue: ZodIssue): string => {
  const message =
    issue.code === "invalid_enum_value" ? "Invalid enum value" : issue.message;
  return issue.path.length > 0
    ? `${issue.path.join(".")}: ${message}`
    : message;
};

const describeIssues = (issues: ZodIssue[]): string[] => {
  const described = issues.slice(0, MAX_REPORTED_ISSUES).map(describeIssue);
  if (issues.length > MAX_REPORTED_ISSUES) {
    described.push(`+${issues.length - MAX_REPORTED_ISSUES} more issues`);
  }
  return described;
};

export class FirestoreDocumentError extends Error {
  readonly documentPath: string;

  readonly issues: string[];

  constructor(documentPath: string, issues: string[]) {
    super(`Invalid document ${documentPath}: ${issues.join("; ")}`);
    this.name = "FirestoreDocumentError";
    this.documentPath = documentPath;
    this.issues = issues;
  }
}

export type ParsedDocument<T> =
  | { data: T & { id: string }; error: null }
  | { data: null; error: FirestoreDocumentError };

const reportedDocuments = new Set<string>();

const reportDocumentError = (error: FirestoreDocumentError): void => {
  if (reportedDocuments.has(error.message)) return;
  if (reportedDocuments.size >= MAX_REPORTED_DOCUMENTS) {
    reportedDocuments.clear();
  }
  reportedDocuments.add(error.message);
  logError(error, {
    document: { path: error.documentPath, issues: error.issues },
  });
};

export const resetReportedDocumentErrors = (): void => {
  reportedDocuments.clear();
};

export const parseDocument = <T extends object>(
  schema: ZodType<T, ZodTypeDef, unknown>,
  snapshot: DocumentSnapshot,
): ParsedDocument<T> => {
  const result = schema.safeParse(snapshot.data());
  if (!result.success) {
    const issues = describeIssues(result.error.issues);
    const error = new FirestoreDocumentError(snapshot.ref.path, issues);
    reportDocumentError(error);
    return { data: null, error };
  }
  return { data: { ...result.data, id: snapshot.id }, error: null };
};

export const parseQueryDocs = <T extends object>(
  schema: ZodType<T, ZodTypeDef, unknown>,
  snapshot: QuerySnapshot,
): { data: (T & { id: string })[]; errors: FirestoreDocumentError[] } => {
  const data: (T & { id: string })[] = [];
  const errors: FirestoreDocumentError[] = [];
  snapshot.docs.forEach((docSnapshot) => {
    const parsed = parseDocument(schema, docSnapshot);
    if (parsed.error) {
      errors.push(parsed.error);
    } else {
      data.push(parsed.data);
    }
  });
  return { data, errors };
};
